/**
 * Shared helpers for grouping T&M / RFI tickets by project and building
 * compact status summaries for Projects Home cards (and Project Page).
 */

import { getLatestInspector } from "../../operations/tmlog/helpers";

/**
 * Return whether a ticket belongs to a project (by job PK or job number).
 *
 * @param {Object} ticket - T&M/RFI API row.
 * @param {Object} project - Project / JobNumber API row.
 * @returns {boolean}
 */
export function isTicketForProject(ticket, project) {
  const projectId = String(project.id);
  const projectNumber = String(project.number || "");
  const ticketJobId = ticket.job == null ? "" : String(ticket.job);
  const ticketJobNumber =
    ticket.job_number == null ? "" : String(ticket.job_number);

  return (
    ticketJobId === projectId ||
    Boolean(projectNumber && ticketJobNumber === projectNumber)
  );
}

/**
 * Build a map of project id → tickets that belong to that project.
 *
 * @param {Array<Object>} projects - Project API rows.
 * @param {Array<Object>} tickets - T&M or RFI API rows.
 * @returns {Map<string, Array<Object>>}
 */
export function groupTicketsByProjectId(projects, tickets) {
  const byProjectId = new Map();
  const projectByJobId = new Map();
  const projectByNumber = new Map();

  for (const project of projects || []) {
    const key = String(project.id);
    byProjectId.set(key, []);
    projectByJobId.set(key, project);
    if (project.number != null && project.number !== "") {
      projectByNumber.set(String(project.number), project);
    }
  }

  for (const ticket of tickets || []) {
    let project = null;
    if (ticket.job != null && ticket.job !== "") {
      project = projectByJobId.get(String(ticket.job)) || null;
    }
    if (!project && ticket.job_number != null && ticket.job_number !== "") {
      project = projectByNumber.get(String(ticket.job_number)) || null;
    }
    if (!project) {
      continue;
    }
    byProjectId.get(String(project.id)).push(ticket);
  }

  return byProjectId;
}

/**
 * Whether a slim T&M project-summary row has pricing (3rd status ball filled).
 *
 * Project summaries expose ``pricing: { total_cost } | null``, not update history.
 *
 * @param {Object} report - T&M project-summary row.
 * @returns {boolean}
 */
function tmReportHasPricing(report) {
  return report?.pricing != null;
}

/**
 * Compact T&M badge counts for a project card (mutually exclusive pipeline stages).
 *
 * Matches TM log status balls that are still gray:
 * - To send: not sent to inspector
 * - To sign: sent, not signed
 * - To price: signed, not priced
 * - To submit: priced, not submitted
 * Submitted reports are omitted.
 *
 * @param {Array<Object>} reports - Project-scoped T&M rows.
 * @returns {{ toSend: number, toSign: number, toPrice: number, toSubmit: number }}
 */
export function buildTmCardSummary(reports) {
  const rows = reports || [];
  let toSend = 0;
  let toSign = 0;
  let toPrice = 0;
  let toSubmit = 0;

  for (const report of rows) {
    if (report.submitted) {
      continue;
    }
    const latestInspector = getLatestInspector(report.inspectors);
    if (tmReportHasPricing(report)) {
      toSubmit += 1;
    } else if (latestInspector?.signed_at) {
      toPrice += 1;
    } else if (latestInspector) {
      toSign += 1;
    } else {
      toSend += 1;
    }
  }

  return { toSend, toSign, toPrice, toSubmit };
}

/**
 * Compact RFI status counts for a project card (mutually exclusive statuses).
 *
 * - To send: ``new`` + ``open`` (Open / Reviewing)
 * - To close: ``awaiting_response``
 * Closed RFIs are omitted.
 *
 * @param {Array<Object>} rfis - Project-scoped RFI rows.
 * @returns {{ toSend: number, toClose: number }}
 */
export function buildRfiCardSummary(rfis) {
  let toSend = 0;
  let toClose = 0;

  for (const rfi of rfis || []) {
    const status = rfi.status || "new";
    if (status === "closed") {
      continue;
    }
    if (status === "awaiting_response") {
      toClose += 1;
    } else {
      // ``new``, ``open``, and unknown map to To send.
      toSend += 1;
    }
  }

  return { toSend, toClose };
}

/**
 * Empty T&M card summary used while tickets load or when a project has none.
 *
 * @returns {{ toSend: number, toSign: number, toPrice: number, toSubmit: number }}
 */
export function emptyTmCardSummary() {
  return { toSend: 0, toSign: 0, toPrice: 0, toSubmit: 0 };
}

/**
 * Empty RFI card summary used while tickets load or when a project has none.
 *
 * @returns {{ toSend: number, toClose: number }}
 */
export function emptyRfiCardSummary() {
  return { toSend: 0, toClose: 0 };
}
